import React, { useState } from 'react';
import { Upload, X, Loader } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CloudinaryUpload({ onUploadSuccess }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 10MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload to Cloudinary
    await uploadToCloudinary(file);
  };

  const uploadToCloudinary = async (file) => {
    setUploading(true);

    try {
      // Get signature from backend
      const token = localStorage.getItem('token');
      const sigResponse = await fetch(`${BACKEND_URL}/api/cloudinary/signature?folder=arthurfitz/products&resource_type=image`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!sigResponse.ok) {
        toast.error('Error al obtener firma');
        setUploading(false);
        return;
      }

      const signData = await sigResponse.json();

      // Upload to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', signData.api_key);
      formData.append('timestamp', signData.timestamp);
      formData.append('signature', signData.signature);
      formData.append('folder', signData.folder);

      const uploadResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${signData.cloud_name}/image/upload`,
        {
          method: 'POST',
          body: formData
        }
      );

      if (!uploadResponse.ok) {
        toast.error('Error al subir imagen');
        setUploading(false);
        return;
      }

      const result = await uploadResponse.json();

      // Return URL to parent
      if (onUploadSuccess) {
        onUploadSuccess(result.secure_url, result.public_id);
      }

      toast.success('Imagen subida correctamente');
      setPreview(null);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Error al subir imagen');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="border-2 border-dashed border-gray-300 rounded p-6 text-center hover:border-black transition-colors">
        <input
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          id="cloudinary-upload"
          disabled={uploading}
        />
        <label htmlFor="cloudinary-upload" className="cursor-pointer">
          {preview ? (
            <div className="relative">
              <img src={preview} alt="Preview" className="max-h-40 mx-auto mb-3" />
              {uploading && (
                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                  <Loader className="w-8 h-8 text-white animate-spin" />
                </div>
              )}
            </div>
          ) : (
            <Upload className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          )}
          <p className="text-sm text-gray-600">
            {uploading ? 'Subiendo...' : 'Click para seleccionar imagen'}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Máx 10MB - JPG, PNG, WebP
          </p>
        </label>
      </div>
      {uploading && (
        <div className="text-center text-sm text-gray-600">
          <Loader className="w-4 h-4 animate-spin inline mr-2" />
          Subiendo a Cloudinary...
        </div>
      )}
    </div>
  );
}
